import React, { useEffect, useRef } from "react";
import { Animated, ScrollView, View } from "react-native";
import { s } from "../SustainTeamManagementScreen.styles";
import { useTheme } from "../../../theme";

const DAY_COL_W    = 104;
const TIME_COL_W   = 110;
const MEMBER_COL_W = 200;

const CalendarViewSkeleton: React.FC = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const pulse = useRef(new Animated.Value(0.45)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1,    duration: 700, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0.45, duration: 700, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  const skeletonColor = isDark ? "#2B2B2B" : "#E4E9F2";

  const skel = (w: number, h: number, radius = 4) => (
    <Animated.View
      style={[
        { opacity: pulse },
        { width: w, height: h, borderRadius: radius, backgroundColor: skeletonColor },
      ]}
    />
  );

  const HeaderRow = () => (
    <View style={[s.calendarHeaderRow, { flexDirection: "row", backgroundColor: isDark ? "#1a1a1a" : "#F7F9FC", borderBottomWidth: 1, borderBottomColor: isDark ? "#404040" : "#E8EDF5" }]}>
      <View style={{ width: MEMBER_COL_W, minHeight: 44, paddingHorizontal: 12, justifyContent: "center" }}>
        {skel(70, 10)}
      </View>
      <View style={{ width: TIME_COL_W, minHeight: 44, alignItems: "center", justifyContent: "center" }}>
        {skel(60, 10)}
      </View>
      <View style={{ width: TIME_COL_W, minHeight: 44, alignItems: "center", justifyContent: "center" }}>
        {skel(60, 10)}
      </View>
      {Array.from({ length: 28 }).map((_, i) => (
        <View key={i} style={{ width: DAY_COL_W, minHeight: 44, alignItems: "center", justifyContent: "center", borderLeftWidth: 1, borderLeftColor: "#EEF2F7" }}>
          {skel(28, 10)}
        </View>
      ))}
    </View>
  );

  const DataRow = ({ even }: { even: boolean }) => (
    <View style={[s.row, isDark && { borderBottomColor: "#363636" }, even ? (isDark ? { backgroundColor: "#202020" } : s.rowEven) : (isDark ? { backgroundColor: "#1a1a1a" } : s.rowOdd), { flexDirection: "row" }]}>
      {/* Member */}
      <View style={{ width: MEMBER_COL_W, minHeight: 44, flexDirection: "row", alignItems: "center", paddingHorizontal: 10, gap: 10 }}>
        <Animated.View style={{ opacity: pulse, width: 28, height: 28, borderRadius: 14, backgroundColor: skeletonColor }} />
        {skel(100, 11)}
      </View>
      {/* Start time */}
      <View style={{ width: TIME_COL_W, minHeight: 44, alignItems: "center", justifyContent: "center", paddingHorizontal: 8 }}>
        {skel(72, 28, 6)}
      </View>
      {/* End time */}
      <View style={{ width: TIME_COL_W, minHeight: 44, alignItems: "center", justifyContent: "center", paddingHorizontal: 8 }}>
        {skel(72, 28, 6)}
      </View>
      {/* Day cells */}
      {Array.from({ length: 28 }).map((_, i) => (
        <View key={i} style={{ width: DAY_COL_W, minHeight: 44, alignItems: "center", justifyContent: "center", borderLeftWidth: 1, borderLeftColor: isDark ? "#363636" : "#EEF2F7" }}>
          {skel(20, 20, 4)}
        </View>
      ))}
    </View>
  );

  return (
    <View style={[s.calendarCard, isDark && { backgroundColor: "#202020" }]}>
      <View style={[s.calendarTableWrap, isDark && { backgroundColor: "#202020" }]}>
        <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            <View>
              <HeaderRow />
              {Array.from({ length: 8 }).map((_, i) => (
                <DataRow key={i} even={i % 2 === 0} />
              ))}
            </View>
          </ScrollView>
        </ScrollView>
      </View>
    </View>
  );
};

export default CalendarViewSkeleton;
